import { useState, useEffect, useRef, useCallback } from 'react';
import { useCopy, fill } from '../../i18n';
import { useProject } from '../../contexts/ProjectContext';
import { useUIState } from '../../contexts/UIStateContext';
import { searchService } from '../../services/searchService';
import analytics from '../../services/analyticsService';
import { cn } from '@/lib/utils';
import type { SearchResults } from '../../types';
import type { LucideIcon } from 'lucide-react';
import {
  Search,
  X,
  CheckSquare,
  AlertTriangle,
  RefreshCw,
  DollarSign,
  ListTodo,
  FolderKanban,
  Loader2
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';

type ResultType = 'tasks' | 'risks' | 'changes' | 'costs' | 'pendencias';

interface SearchResultItem {
  id: string;
  projectId?: string;
  projectName?: string;
  name?: string;
  title?: string;
  description?: string;
  category?: string;
  demanda?: string;
  area?: string;
  type?: string;
  [key: string]: unknown;
}

interface FlatResult {
  type: ResultType;
  item: SearchResultItem;
}

const RESULT_ICONS: Record<ResultType, LucideIcon> = {
  tasks: CheckSquare,
  risks: AlertTriangle,
  changes: RefreshCw,
  costs: DollarSign,
  pendencias: ListTodo
};

// Rótulo do grupo de resultados: reaproveita os nomes de módulo do idioma.
const RESULT_LABEL_KEYS: Record<ResultType, 'risks' | 'changes' | 'costs' | 'todos' | null> = {
  tasks: null,
  risks: 'risks',
  changes: 'changes',
  costs: 'costs',
  pendencias: 'todos',
};

const MODULE_MAP: Record<ResultType, string> = {
  tasks: 'gantt',
  risks: 'risks',
  changes: 'changes',
  costs: 'costs',
  pendencias: 'todos'
};

const RESULT_TYPES: ResultType[] = ['tasks', 'risks', 'changes', 'costs', 'pendencias'];

// Debounce hook
const useDebounce = (value: string, delay: number): string => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchModal = ({ isOpen, onClose }: SearchModalProps) => {
  const copy = useCopy().shell;
  const t = copy.search;
  const { switchProject, focusTask, projects } = useProject();
  const { changeModule } = useUIState();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Debounce the search query
  const debouncedQuery = useDebounce(query, 300);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setQuery('');
      setResults(null);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Perform search when debounced query changes
  useEffect(() => {
    const performSearch = async () => {
      if (debouncedQuery.trim().length < 2) {
        setResults(null);
        return;
      }

      setIsSearching(true);
      try {
        const userProjectIds = (projects || []).map((p: { id: string }) => p.id);
        const searchResults = await searchService.searchGlobal(debouncedQuery, userProjectIds);
        setResults(searchResults);
        setSelectedIndex(0);
        // Termo buscado = roadmap alimentado pelo usuário.
        const total = RESULT_TYPES.reduce((acc, t) => acc + (((searchResults as unknown as Record<string, unknown[]>)[t] || []).length), 0);
        analytics.trackSearch(debouncedQuery, total);
      } catch (err) {
        console.error('Erro na busca:', err);
      } finally {
        setIsSearching(false);
      }
    };

    performSearch();
  }, [debouncedQuery]);

  // Handle search input change
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  // Get flat list of all results for keyboard navigation
  const getFlatResults = useCallback((): FlatResult[] => {
    if (!results) return [];

    const flat: FlatResult[] = [];
    RESULT_TYPES.forEach((type) => {
      ((results[type] || []) as unknown as SearchResultItem[]).forEach((item) => {
        flat.push({ type, item });
      });
    });
    return flat;
  }, [results]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const flatResults = getFlatResults();

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && flatResults.length > 0) {
      e.preventDefault();
      const selected = flatResults[selectedIndex];
      if (selected) {
        handleResultClick(selected.type, selected.item);
      }
    }
  };

  // Handle result click - navigate to the item
  const handleResultClick = (type: ResultType, item: SearchResultItem) => {
    // Switch to the project
    if (item.projectId) {
      switchProject(item.projectId);
    }

    // Change to the appropriate module
    // For tasks, check if it's a milestone
    let module = MODULE_MAP[type];
    if (type === 'tasks' && item.type === 'milestone') {
      module = 'schedule';
    }

    if (module) {
      changeModule(module);
    }

    // Achar a tarefa na busca e cair no módulo sem nada destacado é o mesmo
    // defeito que a notificação tinha: o resultado leva ao lugar certo e o
    // usuário procura de novo. focusTask seleciona e rola até ela assim que o
    // projeto terminar de carregar.
    if (type === 'tasks' && item.projectId && item.id) {
      focusTask(item.projectId, item.id);
    }

    // Close the modal
    onClose();
  };

  // Count total results
  const getTotalResults = (): number => {
    if (!results) return 0;
    return Object.values(results).reduce((sum: number, arr: unknown) => sum + ((arr as SearchResultItem[] | undefined)?.length || 0), 0);
  };

  // Render result item
  const renderResultItem = (type: ResultType, item: SearchResultItem, globalIndex: number) => {
    const Icon = RESULT_ICONS[type];
    const isSelected = globalIndex === selectedIndex;

    // Get display name based on type
    let displayName = '';
    let displayDescription = '';

    switch (type) {
      case 'tasks':
        displayName = item.name || '';
        displayDescription = item.description || '';
        break;
      case 'risks':
        displayName = item.title || item.description?.substring(0, 50) || copy.modules.risks;
        displayDescription = item.description || '';
        break;
      case 'changes':
        displayName = item.title || t.resultChange;
        displayDescription = item.description || '';
        break;
      case 'costs':
        displayName = item.description || t.resultCost;
        displayDescription = item.category || '';
        break;
      case 'pendencias':
        displayName = item.demanda || t.resultPendencia;
        displayDescription = item.area || '';
        break;
      default:
        displayName = item.name || item.title || item.description || '';
    }

    return (
      <div
        key={`${type}-${item.id}`}
        className={cn("flex items-center gap-3 py-2.5 px-5 cursor-pointer transition-colors duration-150 hover:bg-[var(--color-bg-tertiary)]", isSelected && "bg-[var(--color-info-bg-light)]")}
        onClick={() => handleResultClick(type, item)}
      >
        <Icon size={16} className="text-[var(--color-primary)] shrink-0" />
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          <span className="text-sm font-medium text-[var(--color-text)] whitespace-nowrap overflow-hidden text-ellipsis">{displayName}</span>
          {displayDescription && (
            <span className="text-[13px] text-[var(--color-text-muted)] whitespace-nowrap overflow-hidden text-ellipsis">
              {displayDescription.substring(0, 60)}
              {displayDescription.length > 60 ? '...' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 text-[11px] text-[var(--color-text-muted)] shrink-0 [&_svg]:text-[var(--color-border-dark)]">
          <FolderKanban size={12} />
          <span>{item.projectName}</span>
        </div>
      </div>
    );
  };

  let globalIndex = 0;

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-[600px] p-0 gap-0 max-h-[70vh] flex flex-col top-[15vh] translate-y-0 [&>button.absolute]:hidden max-sm:max-w-full max-sm:max-h-screen max-sm:rounded-none">
        <div className="flex items-center gap-3 py-4 px-5 border-b border-[var(--color-border)]">
          {isSearching ? (
            <Loader2 size={20} className="text-[var(--color-text-muted)] shrink-0 animate-spin" />
          ) : (
            <Search size={20} className="text-[var(--color-text-muted)] shrink-0" />
          )}
          <Input
            ref={inputRef}
            type="text"
            className="flex-1 border-none outline-none text-base text-[var(--color-text)] bg-transparent placeholder:text-[var(--color-text-muted)]"
            placeholder={t.placeholder}
            value={query}
            onChange={handleSearch}
            onKeyDown={handleKeyDown}
          />
          <Button variant="icon" size="icon" className="max-md:h-11 max-md:w-11 bg-[var(--color-bg-tertiary)] rounded-[var(--radius-md)] hover:bg-[var(--color-border)]" onClick={onClose}>
            <X size={20} />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto py-2" ref={resultsRef}>
          {query.trim().length < 2 ? (
            <div className="flex flex-col items-center justify-center py-10 px-5 text-[var(--color-text-muted)] text-center [&_svg]:mb-3 [&_svg]:opacity-50">
              <Search size={32} />
              <p className="m-0 text-sm">{t.minChars}</p>
              <span className="mt-2 text-[13px] text-[var(--color-border-dark)]">{t.hint}</span>
            </div>
          ) : isSearching ? (
            <div className="flex flex-col items-center justify-center py-10 px-5 text-[var(--color-text-secondary)] gap-3">
              <Loader2 size={24} className="animate-spin" />
              <p className="m-0 text-sm">{t.searching}</p>
            </div>
          ) : getTotalResults() === 0 ? (
            <div className="flex items-center justify-center py-10 px-5 text-[var(--color-text-secondary)] text-sm">
              <p>{fill(t.noResults, { query })}</p>
            </div>
          ) : (
            <>
              {RESULT_TYPES.map(type => {
                const items = (results?.[type] || []) as unknown as SearchResultItem[];
                if (items.length === 0) return null;

                return (
                  <div key={type} className="mb-2">
                    <div className="flex items-center justify-between py-2 px-5 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)] bg-[var(--color-bg-secondary)]">
                      <span>{RESULT_LABEL_KEYS[type] ? copy.modules[RESULT_LABEL_KEYS[type]!] : t.resultTasks}</span>
                      <span className="text-[11px] font-medium text-[var(--color-text-muted)] bg-[var(--color-border)] py-0.5 px-1.5 rounded-[var(--radius-full)]">{items.length}</span>
                    </div>
                    {items.map((item) => {
                      const element = renderResultItem(type, item, globalIndex);
                      globalIndex++;
                      return element;
                    })}
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div className="flex items-center justify-center gap-6 py-3 px-5 bg-[var(--color-bg-secondary)] border-t border-[var(--color-border)] text-[13px] text-[var(--color-text-secondary)] max-sm:gap-3 max-sm:text-[11px]">
          <span><kbd className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-[11px] font-[inherit] text-[var(--color-text-secondary)] mr-1">&uarr;</kbd><kbd className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-[11px] font-[inherit] text-[var(--color-text-secondary)] mr-1">&darr;</kbd> {t.navigate}</span>
          <span><kbd className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-[11px] font-[inherit] text-[var(--color-text-secondary)] mr-1">Enter</kbd> {t.select}</span>
          <span><kbd className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-[11px] font-[inherit] text-[var(--color-text-secondary)] mr-1">Esc</kbd> {t.close}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;
